/* ==========================================================================
   Proof bar — the scan counter.

   The number is already correct in the HTML before this file runs. Everything
   here is an improvement on a page that is already right:

     · a count-up on first view, so the number lands visually
     · one fetch to the live endpoint, but only when the theme setting says
       CORS has landed
     · a tick, but ONLY on a live number

   That last one is the point. carpm.in sends no Access-Control-Allow-Origin
   today (decision 0001), so the count on screen is the last figure someone
   read, held in a theme setting. Incrementing it every 45 seconds would be
   asserting a rate we have not measured — one sample exists (0003) — in the
   element whose entire job is being the number a sceptical buyer can trust.
   So the fallback never ticks. When the endpoint answers, it does.
   ========================================================================== */
(() => {
  'use strict';

  const el = document.querySelector('[data-gp-scans]');
  if (!el) return;

  const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const fmt = (n) => Math.round(n).toLocaleString('en-US');

  const target = parseInt(el.dataset.gpFallback, 10);
  if (!Number.isFinite(target) || target <= 0) return;

  /* Count up to a value over ~1.6s. Cubic ease-out, transform-free — this is
     text, so there is nothing to composite; it is one textContent write per
     frame on a single node. Under reduced motion the value is simply set. */
  function countTo(value) {
    if (reduced) { el.textContent = fmt(value); return Promise.resolve(); }
    const from = Math.max(0, value - Math.round(value * 0.018));
    const t0 = performance.now();
    const dur = 1600;
    return new Promise((done) => {
      const step = (t) => {
        const p = Math.min(1, (t - t0) / dur);
        const e = 1 - Math.pow(1 - p, 3);
        el.textContent = fmt(from + (value - from) * e);
        if (p < 1) requestAnimationFrame(step);
        else done();
      };
      requestAnimationFrame(step);
    });
  }

  /* Only tick a number this pageview actually fetched. */
  function tick(from) {
    const seconds = parseInt(el.dataset.gpTick, 10);
    if (reduced || !Number.isFinite(seconds) || seconds <= 0) return;
    let n = from;
    setInterval(() => { n += 1; el.textContent = fmt(n); }, seconds * 1000);
  }

  function live() {
    if (el.dataset.gpLive !== '1') return Promise.reject();
    const url = el.dataset.gpEndpoint;
    if (!url) return Promise.reject();
    // 4s is generous against the 0.2–1.0s the endpoint measures at, and the
    // page is already correct if it expires.
    const ctl = typeof AbortController === 'function' ? new AbortController() : null;
    const timer = ctl ? setTimeout(() => ctl.abort(), 4000) : null;
    return fetch(url, { credentials: 'omit', signal: ctl && ctl.signal })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(r.status))))
      .then((d) => {
        const n = parseInt(d && d.count, 10);
        if (!Number.isFinite(n) || n <= 0) throw new Error('no count');
        return n;
      })
      .finally(() => { if (timer) clearTimeout(timer); });
  }

  /* Animate on first view, not on load — the bar sits under a 430px photo
     band, so on a phone the count-up would otherwise play off screen. */
  let played = false;
  const run = () => {
    if (played) return;
    played = true;
    live()
      .then((n) => countTo(n).then(() => tick(n)))
      .catch(() => countTo(target));
  };

  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { io.disconnect(); run(); }
    }, { threshold: 0.4 });
    io.observe(el);
  } else {
    run();
  }
})();
